import {Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormWrapper} from "./items/form.wrapper";
import {ReactiveFormsModule, ValidationErrors} from "@angular/forms";

import {TranslatePipe} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {isDefined, nonBlank, unsubscribe} from "../utils/epic.utils";
import {LoggingService} from "../../services/logger/logging.service";
import {filter} from "rxjs/operators";
import {FormFooterComponent} from "./form-footer/form-footer.component";
import {ExceptionErrorDto} from "../../repositories/dto/exception-error.dto";
import {I18nService} from "../../services/i18n/i18n.service";

@Component({
  selector: 'acc-form',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    FormFooterComponent
  ],
  templateUrl: './form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form.component.css'
})
export class FormComponent implements OnInit, OnDestroy {

  @ViewChild('submit')
  private submitElement: ElementRef<HTMLInputElement> | undefined;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input()
  public showFooter: boolean = true;

  protected readonly log: LoggingService = inject(LoggingService);

  private readonly i18n: I18nService = inject(I18nService);

  private readonly subs: Subscription[] = [];
  private readonly generalErrorCodes: string[] = ['atLeastOneRequired'];

  ngOnInit(): void {
    this.subs.push(this.wrapper.onChangeDelayed()
      .pipe(
        filter(() => this.wrapper.isAutoSubmit()),
        filter((changed) => changed.notInitialValue())
      )
      .subscribe(() => this.submitForm()));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected isViewOnly(): boolean {
    return this.wrapper.isViewOnly();
  }

  protected onFormSubmit(): void {
    if (this.wrapper.isAutoSubmit()) {
      this.log.trace('FormComponent: submit form: {}', [this.wrapper.form.value]);
      this.wrapper.submit();
    }
  }

  protected hasGeneralErrors(): boolean {
    return !this.isViewOnly() && this.getGeneralErrors().length > 0;
  }

  protected getGeneralErrors(): string[] {
    if (this.wrapper.isReady) {
      const errors: ValidationErrors | null = this.wrapper.form.errors;
      if (isDefined(errors)) {
        return this.generalErrorCodes.filter((c) => errors[c]);
      }
    }
    return [];
  }

  protected buildExceptionMessage(ex: ExceptionErrorDto): string {
    if (nonBlank(ex.message)) {
      return this.i18n.translate(ex.message!);
    } else if (ex.code === '50000' && nonBlank(ex.additionalMessage)) {
      return this.i18n.translate('vatrate.error.notFound.withName', {
        name: this.i18n.translate('vatrate.' + ex.additionalMessage?.toLowerCase())
      });
    } else if (ex.code === '50000') {
      return this.i18n.translate('vatrate.error.notFound');
    }
    return this.i18n.translate('exception.unknown');
  }

  private submitForm(): void {
    this.submitElement?.nativeElement?.click();
  }
}
